// client/src/coms/settings/sections/NotificationSettings.js
// Personal Settings → Notifications: Do Not Disturb (account), desktop notifications for this browser, and where
// per-server unread badges are set.
import React, { useCallback, useEffect, useState } from 'react';
import { Row, Section, Switch } from '../../personalisation/settingsControls';
import DndSetting from '../../components/DndSetting';

const LS_PUSH_DISABLED = 'vox:push:disabled';

function pushSupport() {
  if (typeof window === 'undefined') return 'unsupported';
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  if (!window.isSecureContext) return 'unsupported';
  return Notification.permission; // 'default' | 'granted' | 'denied'
}

async function currentSubscription() {
  try {
    const reg = await navigator.serviceWorker.getRegistration('/');
    return reg ? await reg.pushManager.getSubscription() : null;
  } catch {
    return null;
  }
}

function useDesktopNotifications() {
  const [permission, setPermission] = useState(() => pushSupport());
  const [subscribed, setSubscribed] = useState(null);
  const [disabled, setDisabled] = useState(() => {
    try {
      return window.localStorage.getItem(LS_PUSH_DISABLED) === '1';
    } catch {
      return false;
    }
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const refresh = useCallback(async () => {
    setPermission(pushSupport());
    if (pushSupport() === 'unsupported') return;
    setSubscribed(!!(await currentSubscription()));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const enable = async () => {
    setBusy(true);
    setError('');
    try {
      try {
        window.localStorage.removeItem(LS_PUSH_DISABLED);
      } catch {}
      setDisabled(false);
      let perm = Notification.permission;
      if (perm === 'default') perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== 'granted') {
        setError(perm === 'denied' ? 'Notifications are blocked for this site. Allow them from the padlock in the address bar.' : 'Notifications were not allowed.');
        return;
      }
      window.dispatchEvent(new Event('vox-push-sync'));
      for (let i = 0; i < 10; i += 1) {
        // The subscription is created by the app in the background; give it a moment.
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 400));
        // eslint-disable-next-line no-await-in-loop
        if (await currentSubscription()) break;
      }
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    setError('');
    try {
      try {
        window.localStorage.setItem(LS_PUSH_DISABLED, '1');
      } catch {}
      setDisabled(true);
      const sub = await currentSubscription();
      if (sub) {
        await fetch('/api/coms/push/unsubscribe', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: sub.endpoint }),
        }).catch(() => {});
        await sub.unsubscribe().catch(() => {});
      }
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const on = permission === 'granted' && !disabled && subscribed !== false;
  return { permission, on, busy, error, enable, disable };
}

export default function NotificationSettings({ serverName, onOpenCategory }) {
  const push = useDesktopNotifications();
  return (
    <Section id="vx-cat-notifications" title="Notifications" description="When Voxemera may get your attention.">
      <DndSetting />
      <Row
        label="Desktop notifications on this device"
        hint={
          push.permission === 'unsupported'
            ? 'This browser cannot show notifications from websites. Install Voxemera as an app, or use another browser.'
            : push.permission === 'denied'
              ? 'Blocked by your browser for this site. Allow notifications from the padlock in the address bar, then turn this on.'
              : 'Mentions, replies and direct messages, even when Voxemera is in the background.'
        }
        settingId="desktop-notifications"
        scope="device"
      >
        <Switch label="Desktop notifications on this device" checked={push.on} onChange={(v) => (v ? push.enable() : push.disable())} testId="settings-desktop-notifications" />
        {push.busy ? <span className="vx-set-hint">Working…</span> : null}
        {push.error ? (
          <div className="vx-acct-error" role="alert">
            {push.error}
          </div>
        ) : null}
      </Row>
      <Row
        label="Unread badges"
        hint={`Choose per server whether channels show all unread messages, only mentions, or nothing${serverName ? ` (for ${serverName})` : ''}.`}
        settingId="unread-badges"
        scope="server"
      >
        <button type="button" className="vx-btn small" onClick={() => onOpenCategory('server', 'server-prefs')}>
          Open This Server
        </button>
      </Row>
    </Section>
  );
}
